import React, { useState, useEffect, useLayoutEffect } from 'react';

import { useQueries, useQuery } from '@tanstack/react-query';

import TicketsList from './TicketsList';
import Messages from './Messages';

import { useUiStore } from '../../../../../store';
import TicketService from '../../../../../services/TicketService';
import LoanService from '../../../../../services/LoanService';
import FreshcallerCallService from '../../../../../services/FreshcallerCallService';

export default function Conversations({ borrowers, isLender, loanId }) {
  const [inboxSidebarOpen, setInboxSidebarOpen] = useState(false);
  const [currentTicketId, setCurrentTicketId] = useState(null);
  const [ticketsAndCalls, setTicketsAndCalls] = useState([]);
  const [currentCallId, setCurrentCallId] = useState(null);

  const { data: borrowersData } = useQuery({
    queryKey: ['borrowers', loanId],
    queryFn: () => LoanService.getLoanBorrowers(loanId),
    enabled: !!isLender,
  });

  const borrowersSource = isLender ? borrowersData : borrowers;

  const borrowerEmails = borrowersSource
    ?.map((borrower) => borrower._borrower?.email || borrower.email)
    .filter(Boolean);

  const tickets = useQueries({
    queries: borrowerEmails.map((borrowerEmail) => ({
      queryKey: ['tickets', borrowerEmail],
      queryFn: () => TicketService.getTickets(borrowerEmail),
    })),
    combine: (results) => {
      return {
        data: results
          .map((result) => {
            return result.data;
          })
          .filter(Boolean)
          .filter(Array.isArray)
          .flat(),
        isPending: results.some((result) => result.isPending),
      };
    },
  });

  const calls = useQueries({
    queries: borrowerEmails.map((borrowerEmail) => ({
      queryKey: ['calls', borrowerEmail],
      queryFn: () => FreshcallerCallService.getFreshcallerCalls(borrowerEmail),
    })),
    combine: (results) => {
      return {
        data: results
          .map((result) => {
            return result.data;
          })
          .filter(Boolean)
          .filter(Array.isArray)
          .flat(),
        isPending: results.some((result) => result.isPending),
      };
    },
  });

  const setIsMainOverflowHidden = useUiStore(
    (state) => state.setIsMainOverflowHidden
  );

  useLayoutEffect(() => {
    setIsMainOverflowHidden(true);

    return () => setIsMainOverflowHidden(false);
  }, [inboxSidebarOpen, setIsMainOverflowHidden]);

  // Combine tickets and calls into one array
  useEffect(() => {
    if (!tickets.isPending && !calls.isPending) {
      let combined = [
        ...tickets.data,
        ...calls.data.map((call) => {
          const callDirection = call.call_data?.direction;
          const capitalizedDirection =
            // eslint-disable-next-line no-unsafe-optional-chaining
            callDirection?.charAt(0).toUpperCase() + callDirection?.slice(1);

          const callParticipant = call.call_data.participants.find(
            (prtcpnt) => prtcpnt.participant_type !== 'Agent'
          );
          return {
            ...call,
            created_at: call.call_data?.created_time,
            requester: {
              email: callParticipant?.caller_number,
            },
            subject: `${capitalizedDirection} Phone Call`,
            description_text:
              call.transcript || 'There is no transcript for this call',
          };
        }),
      ];

      combined = combined.toSorted((a, b) => {
        return new Date(b.created_at) - new Date(a.created_at);
      });

      setTicketsAndCalls(combined);
    }
  }, [tickets, calls]);

  return (
    <div className="relative flex">
      {/* Inbox sidebar */}
      <TicketsList
        inboxSidebarOpen={inboxSidebarOpen}
        setInboxSidebarOpen={setInboxSidebarOpen}
        tickets={ticketsAndCalls}
        setCurrentTicketId={setCurrentTicketId}
        currentTicketId={currentTicketId}
        setCurrentCallId={setCurrentCallId}
        currentCallId={currentCallId}
      />

      {/* Inbox body */}
      <Messages
        inboxSidebarOpen={inboxSidebarOpen}
        setInboxSidebarOpen={setInboxSidebarOpen}
        currentTicketId={currentTicketId}
        currentCall={ticketsAndCalls.find(
          (ticket) => ticket.id === currentCallId
        )}
      />
    </div>
  );
}
